import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../../context/searchContext";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../../hooks/useListFilters";
import useI18n from "../../../../../../hooks/useTranslations";
import { Shuttle } from "../../../../../../http/vertigrip/types/shuttles";
import { useShuttlesApi } from "../../../../../../http/vertigrip/useShuttles";
import { PaginationQuery } from "../../../../../../types";
import FlexContent from "../../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../../ui/ListItemMenu";
import ModalDialog from "../../../../../ui/ModalDialog";
import SearchBar from "../../../../../ui/SearchBar";
import Table from "../../../../../ui/Table";
import Visibility from "../../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";
import CreateShuttleModalDialogButton from "./CreateShuttleModalDialogButton";
import EditShuttleModalDialog from "./EditShuttleModalDialog";

type Props = {
  active?: boolean;
};

const Shuttles: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { systemTypeId } = useParams<{ systemTypeId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "shuttles" });
  const aborter = useRef(new AbortController());
  const shuttlesApi = useShuttlesApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "shuttles");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await shuttlesApi.updateAdminShuttleVisibility(id, { visibility }, { systemTypeId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (shuttleId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await shuttlesApi.updateAdminShuttleOrder(shuttleId, { order: newOrder }, { systemTypeId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToDelete, setItemToDelete] = useState<Shuttle>();
  const { accessesAction, accessesDialog } = useEntityAccesses<Shuttle>("vertigrip.shuttle");
  const [itemToEdit, setItemToEdit] = useState<Shuttle>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = shuttlesApi.useAdminSystemTypeShuttles(systemTypeId!, query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.shuttles.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.shuttles.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateShuttleModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<Shuttle>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.id}`}>
                {item.code}
              </Link>
            </td>
            <td>{item.description}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.shuttles.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                shuttlesApi
                  .deleteAdminShuttle(itemToDelete.id, itemToDelete.systemType.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.shuttles.questions.delete}
        </ModalDialog>
      )}
      <EditShuttleModalDialog onClose={() => setItemToEdit(undefined)} shuttle={itemToEdit} />
    </FlexContent>
  );
};

export default Shuttles;
